/**
 * Make truncated names less frustrating by allowing users to see the full text.
 * The actual truncation happens via CSS in app/frontend/jose_modern/css/inventory/_table_constraints.scss.
 * This JS adds interactivity so users can always see the full name when needed.
 *
 * Behavior:
 * - Fine pointer devices (mouse): Hover shows tooltip via tippy.js.
 * - All devices: Clicking/tapping any truncated name removes truncation from ALL names in the
 *   table so they can be compared at a glance. Once expanded, clicking a name navigates normally.
 *   Lasts until page reload.
 */

import tippy from "tippy.js";

const TRUNCATED_SELECTOR = ".truncated-name, .truncated-name-fluid";
const TABLE_WRAPPER_SELECTOR = "[data-ajax-table]";
const TRUNCATION_DISABLED_CLASS = "truncation-disabled";
const RESIZE_DEBOUNCE_MS = 250;

let resizeTimeoutId;

function isInventoryView() {
  return document.body?.classList.contains("inventory-view");
}

function isTruncated(element) {
  const hasOverflow = element.scrollWidth > element.offsetWidth;
  const link = element.querySelector("a");
  if (link) {
    return hasOverflow || link.scrollWidth > link.offsetWidth;
  }
  return hasOverflow;
}

function clearHintAttributes(element) {
  if (element.dataset.hint) {
    delete element.dataset.hint;
    element.removeAttribute("data-hint");
  }
  if (element.dataset.hintHtml) {
    delete element.dataset.hintHtml;
    element.removeAttribute("data-hint-html");
  }
}

function destroyTippysInContainer(container) {
  container.querySelectorAll(TRUNCATED_SELECTOR).forEach((el) => {
    if (el._tippy) el._tippy.destroy();
  });
}

// Clicking/tapping any truncated name expands all names in the same table.
// Uses capture phase to intercept before link navigation.
function handleTruncatedClick(event) {
  if (!isInventoryView()) return;

  const truncatedEl = event.target.closest(TRUNCATED_SELECTOR);
  if (!truncatedEl) return;

  const wrapper = truncatedEl.closest(TABLE_WRAPPER_SELECTOR);
  if (!wrapper) return;

  // Already expanded - let the click navigate normally
  if (wrapper.classList.contains(TRUNCATION_DISABLED_CLASS)) return;

  // Name fits in the column - let the click navigate normally
  if (!isTruncated(truncatedEl)) return;

  event.preventDefault();

  // Snapshot the name column's rendered width so the table doesn't reflow
  // when truncation is removed. We set a CSS custom property on the wrapper
  // and use it in the stylesheet (with !important) because table-layout:auto
  // ignores inline max-width on cells.
  const firstNameCell = wrapper.querySelector(".item-name-column");
  if (firstNameCell) {
    wrapper.style.setProperty("--name-col-w", firstNameCell.offsetWidth + "px");
  }

  wrapper.classList.add(TRUNCATION_DISABLED_CLASS);
  destroyTippysInContainer(wrapper);
}

export function scanTruncatedElements(container = document) {
  if (!container || typeof container.querySelectorAll !== "function") return;

  const elements = container.querySelectorAll(TRUNCATED_SELECTOR);
  if (elements.length === 0) {
    return;
  }

  const isFinePointer = window.matchMedia("(pointer: fine)").matches;

  elements.forEach((element) => {
    const isCurrentlyTruncated = isTruncated(element);

    clearHintAttributes(element);

    if (isFinePointer) {
      const hasTippy = !!element._tippy;

      if (isCurrentlyTruncated && !hasTippy) {
        const fullText = element.getAttribute("title") || element.textContent.trim();
        const originalTitle = element.getAttribute("title");

        if (originalTitle) {
          element.removeAttribute("title");
        }

        const tooltipContent = fullText + "<br><br>Click to expand all truncated names";

        tippy(element, {
          content: tooltipContent,
          allowHTML: true,
          placement: "top",
          arrow: true,
          delay: [0, 0],
          theme: "recipal",
          trigger: "mouseenter focus",
          hideOnClick: false,
          onDestroy: () => {
            if (originalTitle) {
              element.setAttribute("title", originalTitle);
            }
          },
        });
      } else if (!isCurrentlyTruncated && hasTippy) {
        element._tippy.destroy();
      }
    }
  });
}

function handlePartialReplace(event) {
  if (!event?.container) return;

  // If truncation was expanded in this wrapper, keep it that way
  const wrapper = event.container.closest?.(TABLE_WRAPPER_SELECTOR) || event.container;
  if (wrapper.classList?.contains(TRUNCATION_DISABLED_CLASS)) return;

  scanTruncatedElements(event.container);
}

function handleResize() {
  clearTimeout(resizeTimeoutId);
  resizeTimeoutId = window.setTimeout(() => {
    if (!isInventoryView()) return;
    scanTruncatedElements(document);
  }, RESIZE_DEBOUNCE_MS);
}

function initializeTruncation() {
  if (!isInventoryView()) return;

  scanTruncatedElements(document);

  document.addEventListener("partial:replace", handlePartialReplace);
  window.addEventListener("resize", handleResize);

  document.addEventListener("click", handleTruncatedClick, true);
}

if (["interactive", "complete"].includes(document.readyState)) {
  initializeTruncation();
} else {
  document.addEventListener("DOMContentLoaded", initializeTruncation);
}
